import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  clampCallOverlayHeight,
  resolveInitialCallOverlayHeight,
  writeStoredCallOverlayHeight,
} from '../services/callOverlayPreferences';

export interface UseCallOverlayResizeOptions {
  disabled?: boolean;
}

/** Height change (px) applied per Arrow-key press when resizing via keyboard. */
const KEYBOARD_RESIZE_STEP_PX = 10;

export interface CallOverlayResizeHandleProps {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
}

export function useCallOverlayResize(options: UseCallOverlayResizeOptions = {}) {
  const { disabled = false } = options;
  const [heightPx, setHeightPx] = useState(resolveInitialCallOverlayHeight);
  const [committedHeightPx, setCommittedHeightPx] = useState(resolveInitialCallOverlayHeight);
  const dragRef = useRef<{ startY: number; startHeight: number; pointerId: number } | null>(null);

  useEffect(() => {
    const onWindowResize = () => {
      setHeightPx((current) => {
        const clamped = clampCallOverlayHeight(current);
        setCommittedHeightPx(clamped);
        return clamped;
      });
    };
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, []);

  const finishDrag = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setHeightPx((current) => {
      const clamped = clampCallOverlayHeight(current);
      writeStoredCallOverlayHeight(clamped);
      setCommittedHeightPx(clamped);
      return clamped;
    });
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (disabled) return;
    event.preventDefault();
    dragRef.current = {
      startY: event.clientY,
      startHeight: heightPx,
      pointerId: event.pointerId,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [disabled, heightPx]);

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    const deltaY = event.clientY - dragRef.current.startY;
    setHeightPx(clampCallOverlayHeight(dragRef.current.startHeight + deltaY));
  }, []);

  const onKeyDown = useCallback((event: ReactKeyboardEvent<HTMLElement>) => {
    if (disabled) return;
    let delta = 0;
    if (event.key === 'ArrowUp') delta = -KEYBOARD_RESIZE_STEP_PX;
    else if (event.key === 'ArrowDown') delta = KEYBOARD_RESIZE_STEP_PX;
    else return;
    event.preventDefault();
    setHeightPx((current) => {
      const clamped = clampCallOverlayHeight(current + delta);
      writeStoredCallOverlayHeight(clamped);
      setCommittedHeightPx(clamped);
      return clamped;
    });
  }, [disabled]);

  const resizeHandleProps: CallOverlayResizeHandleProps = {
    onPointerDown,
    onPointerMove,
    onPointerUp: finishDrag,
    onPointerCancel: finishDrag,
    onKeyDown,
  };

  return { heightPx, committedHeightPx, resizeHandleProps };
}
